/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React from "react";
import Form from "../ui/form/Form";
import { useForm } from "react-hook-form";
import FormError from "../ui/form/FormError";
import Button from "../ui/Button";
import { useClientFields } from "../../lib/react-query/useClientFields";
import { useClientTerms } from "../../lib/react-query/useClientTerms";
import { useCountries } from "../../lib/react-query/useCountries";
import { useConfig } from "../../lib/react-query/useConfig";
import LoadingIndicator from "../LoadingIndicator";
import FormFieldsWrapper from "../ui/form/FormFieldsWrapper";
import ClientTermsCheckboxes from "./form/ClientTermsCheckboxes";
import Anchor from "../ui/Anchor";
import ButtonGroup from "../ui/ButtonGroup";
import Text from "../ui/Text";
import ClientFieldsGroup from "../client/ClientFieldsGroup";
import T from "../ui/Translation";
import { useAuth } from "../../hooks/useAuth";
import { extractErrorMessage, extractFieldErrors } from "../../utils/api/apiErrors";

const ClientRegisterForm = ({ onSubmit, showLoginLink, onLoginClick }) => {
  const form = useForm({
    defaultValues: {
      name: "",
      email: "",
      password: "",
      phone: "",
    },
  });
  const {
    handleSubmit,
    setError,
    formState: { errors },
  } = form;
  const { register, isRegisterPending } = useAuth();
  const { data: clientFields, isLoading: clientFieldsLoading } = useClientFields();
  const { data: terms, isLoading: termsLoading } = useClientTerms({
    enabled: !!clientFields,
  });
  const { data: config } = useConfig();
  const { isLoading: countriesLoading } = useCountries({ enabled: !!config?.client_login_allow_address });

  const handleRegister = async (data) => {
    try {
      const clientData = await register(data);
      onSubmit?.(clientData);
    } catch (error) {
      const fieldErrors = Object.entries(extractFieldErrors(error));

      if (fieldErrors.length > 0) {
        fieldErrors.forEach(([fieldName, fieldError]) => {
          setError(fieldName, {
            message: fieldError.toString(),
          });
        });
      } else {
        setError("root", {
          message: extractErrorMessage(error, "An error occurred during registration"),
        });
      }
    }
  };

  if (clientFieldsLoading || termsLoading || countriesLoading) {
    return <LoadingIndicator page />;
  }

  return (
    <Form onSubmit={handleSubmit(handleRegister)} noValidate>
      <FormFieldsWrapper>
        <ClientFieldsGroup form={form} fields={clientFields} isRegisterForm />
        <ClientTermsCheckboxes form={form} terms={terms} />
      </FormFieldsWrapper>
      <ButtonGroup displayAs="column" itemsWidth="full">
        <Button variant="primary" title="Sign up" loading={isRegisterPending} />
        {showLoginLink && (
          <Text size="small">
            <Anchor onClick={onLoginClick}><T>Already have an account? Log in</T></Anchor>
          </Text>
        )}
      </ButtonGroup>
      {errors.root && <FormError>{errors.root.message}</FormError>}
    </Form>
  );
};

export default ClientRegisterForm;
